import { cx } from "@shared/components/untitledui/util/cx"
import type { DragEvent as ReactDragEvent, ReactNode, RefObject } from "react"
import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { suppressStrayLessonDrops } from "./suppress-stray-lesson-drops"
import styles from "./use-lesson-drag-ghost.module.css"

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max))
}

/** Everything captured at dragstart that shapes and positions the follower card. */
interface GhostGrab {
  /** Pointer offset within the grabbed header (clamped into the card once measured). */
  pointerFromAnchorX: number
  pointerFromAnchorY: number
  /** Latest pointer position (viewport coords), fed by the document's dragover stream. */
  x: number
  y: number
  /** Grab point within the card: the pointer stays pinned to it; the tilt pivots on it. */
  grabX: number
  grabY: number
  /** The grabbed header's width and font, mirrored onto the card. */
  width: number
  fontFamily: string
}

const IDLE_GRAB: GhostGrab = {
  pointerFromAnchorX: 0,
  pointerFromAnchorY: 0,
  x: 0,
  y: 0,
  grabX: 0,
  grabY: 0,
  width: 320,
  fontFamily: "",
}

export interface LessonDragGhost {
  /** Render this alongside the slot: the follower card portal (null while not dragging). */
  element: ReactNode
  /** Merge onto the grip's props (after react-aria's dragProps): spawns the ghost on a
      genuine pointer dragstart. */
  spawnProps: { onDragStart: (event: ReactDragEvent<HTMLElement>) => void }
  /** Hand this to useLessonDragSource — react-aria's onDragEnd tears the ghost down. */
  onDragEnd: () => void
}

/**
 * The lesson drag ghost: an opaque tilted follower card in a portal, because a native drag
 * image can be neither (Chrome dims every setDragImage bitmap; a transform drifts the raster
 * off the grab point). Coordinates ride the document's `dragover` stream, NOT react-aria's
 * onDragMove — Firefox reports (0, 0) client coordinates on the underlying `drag` events
 * (pinned by the browser-firefox run of drag-ghost-paint.browser.test.tsx). Each dragover
 * writes the translate directly; the only animation is the one-shot pickup tilt.
 */
export function useLessonDragGhost(
  anchorRef: RefObject<HTMLDivElement | null>,
  content: ReactNode,
): LessonDragGhost {
  const [isVisible, setVisible] = useState(false)
  const [isPickedUp, setPickedUp] = useState(false)
  const followerRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const grabRef = useRef<GhostGrab>(IDLE_GRAB)
  const rafRef = useRef<number | null>(null)

  const position = () => {
    const follower = followerRef.current
    if (!follower) return
    const grab = grabRef.current
    follower.style.transform = `translate3d(${grab.x - grab.grabX}px, ${grab.y - grab.grabY}px, 0)`
  }

  const spawnProps = {
    onDragStart: (event: ReactDragEvent<HTMLElement>) => {
      // react-aria preventDefaults virtual (screen-reader) drags — no pointer to follow.
      // Keyboard drags never fire a native dragstart, so neither mounts a ghost.
      if (event.defaultPrevented) return
      const anchor = anchorRef.current
      if (!anchor) return
      const rect = anchor.getBoundingClientRect()
      grabRef.current = {
        pointerFromAnchorX: event.clientX - rect.left,
        pointerFromAnchorY: event.clientY - rect.top,
        x: event.clientX,
        y: event.clientY,
        grabX: 0,
        grabY: 0,
        width: anchor.offsetWidth,
        fontFamily: getComputedStyle(anchor).fontFamily,
      }
      setVisible(true)
    },
  }

  const onDragEnd = () => setVisible(false)

  // Follow the pointer via the document's dragover stream — capture phase, so no drop
  // target's stopPropagation can starve the ghost of coordinates.
  useEffect(() => {
    if (!isVisible) return
    document.body.classList.add("lesson-drag-in-flight")
    const onDragOver = (event: DragEvent) => {
      grabRef.current.x = event.clientX
      grabRef.current.y = event.clientY
      // One write per frame; dragover can outpace the display.
      rafRef.current ??= requestAnimationFrame(() => {
        rafRef.current = null
        position()
      })
    }
    document.addEventListener("dragover", onDragOver, { capture: true })
    // A drag in flight is also when a drop in dead space could navigate the page away.
    const stopSuppressingStrayDrops = suppressStrayLessonDrops()
    return () => {
      document.removeEventListener("dragover", onDragOver, { capture: true })
      stopSuppressingStrayDrops()
      document.body.classList.remove("lesson-drag-in-flight")
    }
  }, [isVisible])

  // Before first paint: pin the grab point (the pointer's offset within the header,
  // clamped into the measured card so the pointer always rides ON the card — the grip
  // sits below the header's box) and place the card under the pointer.
  useLayoutEffect(() => {
    if (!isVisible) return
    const follower = followerRef.current
    const card = cardRef.current
    if (!follower || !card) return
    const inset = 12
    const grab = grabRef.current
    grab.grabX = clamp(grab.pointerFromAnchorX, inset, follower.offsetWidth - inset)
    grab.grabY = clamp(grab.pointerFromAnchorY, inset, follower.offsetHeight - inset)
    card.style.transformOrigin = `${grab.grabX}px ${grab.grabY}px`
    position()
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [isVisible])

  // Pick the card up on the next frame, so the untilted state paints first and the
  // tilt transition has a start point.
  useEffect(() => {
    if (!isVisible) return
    const raf = requestAnimationFrame(() => setPickedUp(true))
    return () => {
      cancelAnimationFrame(raf)
      setPickedUp(false)
    }
  }, [isVisible])

  const element = isVisible
    ? createPortal(
        <div
          ref={followerRef}
          // Positioned by direct translate writes as the dragover stream arrives. Deliberately
          // NO transition — smoothing the translate makes the card trail behind the pointer.
          className="pointer-events-none fixed top-0 left-0 z-9999 will-change-transform"
          style={{ width: grabRef.current.width, fontFamily: grabRef.current.fontFamily }}
        >
          {/* Nested inside the follower so the pickup tilt's transition never smooths the
              follower's translate. */}
          <div
            ref={cardRef}
            data-test="lesson drag ghost"
            className={cx(
              "rounded-lg bg-white p-3 text-[0.9375rem] leading-none shadow-drag-ghost",
              styles.ghostCard,
              isPickedUp && styles.ghostCardPickedUp,
            )}
          >
            {content}
          </div>
        </div>,
        document.body,
      )
    : null

  return { element, spawnProps, onDragEnd }
}
